import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { 
    Shield, Plus, Edit, Trash2, Users, Check, X, 
    Loader2, Lock, ChevronDown, ChevronUp 
} from 'lucide-react';

/**
 * RolesPage - Role and permissions management
 * Allows creating roles and assigning permissions
 */
export default function RolesPage() {
    const [roles, setRoles] = useState([]);
    const [permisos, setPermisos] = useState([]);
    const [permisosGrouped, setPermisosGrouped] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [expandedRole, setExpandedRole] = useState(null);
    const [rolePermisos, setRolePermisos] = useState({});
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        permisos: []
    });

    const token = localStorage.getItem('token');
    const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [rolesRes, permisosRes] = await Promise.all([
                fetch(`${API_URL}/api/roles`, { headers }),
                fetch(`${API_URL}/api/roles/permisos/grouped`, { headers })
            ]);

            if (!rolesRes.ok) throw new Error('Error al cargar roles');

            const rolesData = await rolesRes.json();
            setRoles(rolesData);
            
            if (permisosRes.ok) {
                const permisosData = await permisosRes.json();
                console.log('Permisos cargados:', permisosData);
                setPermisosGrouped(permisosData);
            } else {
                console.error('Error cargando permisos:', permisosRes.status);
            }
        } catch (err) {
            console.error('Error en fetchData:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchRolePermisos = async (roleId) => {
        if (rolePermisos[roleId]) {
            setExpandedRole(expandedRole === roleId ? null : roleId);
            return;
        }

        try {
            const res = await fetch(`${API_URL}/api/roles/${roleId}`, { headers });
            if (res.ok) {
                const data = await res.json();
                setRolePermisos(prev => ({ ...prev, [roleId]: data.permisos || [] }));
                setExpandedRole(roleId);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const url = editingId 
            ? `${API_URL}/api/roles/${editingId}`
            : `${API_URL}/api/roles`;
        const method = editingId ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers,
                body: JSON.stringify(formData)
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Error al guardar');
            }

            setModalOpen(false);
            resetForm();
            fetchData();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar este rol? Solo se puede eliminar si no tiene usuarios asignados.')) return;

        try {
            const res = await fetch(`${API_URL}/api/roles/${id}`, {
                method: 'DELETE',
                headers
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Error al eliminar');
            }

            fetchData();
        } catch (err) {
            alert(err.message);
        }
    };

    const openEdit = async (role) => {
        try {
            const res = await fetch(`${API_URL}/api/roles/${role.id}`, { headers });
            if (res.ok) {
                const data = await res.json();
                setFormData({
                    nombre: data.nombre || '',
                    descripcion: data.descripcion || '',
                    permisos: data.permisos?.map(p => p.id) || []
                });
                setEditingId(role.id);
                setModalOpen(true);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const resetForm = () => {
        setFormData({ nombre: '', descripcion: '', permisos: [] });
        setEditingId(null);
    };

    const togglePermiso = (permisoId) => {
        setFormData(prev => ({
            ...prev,
            permisos: prev.permisos.includes(permisoId)
                ? prev.permisos.filter(id => id !== permisoId)
                : [...prev.permisos, permisoId]
        }));
    };

    const getModuloIcon = (modulo) => {
        const icons = {
            pos: '🛒',
            productos: '📦',
            categorias: '🏷️',
            ventas: '💵',
            preventas: '📝',
            compras: '🛍️',
            proveedores: '🚚',
            promociones: '🎯',
            cajas: '💰',
            reportes: '📊',
            usuarios: '👤',
            colaboradores: '👥',
            roles: '🛡️',
            turnos: '📅'
        };
        return icons[modulo] || '📋';
    };

    const getModuloNombre = (modulo) => {
        const nombres = {
            pos: 'Punto de Venta',
            productos: 'Productos',
            categorias: 'Categorías',
            ventas: 'Ventas',
            preventas: 'Preventas',
            compras: 'Compras',
            proveedores: 'Proveedores',
            promociones: 'Promociones',
            cajas: 'Cajas',
            reportes: 'Reportes',
            usuarios: 'Usuarios',
            colaboradores: 'Colaboradores',
            roles: 'Roles',
            turnos: 'Turnos'
        };
        return nombres[modulo] || modulo;
    };

    // Group permissions by module for the form
    const getPermisosForModulo = (modulo, permisosList) => {
        return {
            ver: permisosList.find(p => p.codigo === `${modulo}_ver`),
            editar: permisosList.find(p => p.codigo === `${modulo}_editar`),
            eliminar: permisosList.find(p => p.codigo === `${modulo}_eliminar`)
        };
    };

    if (loading) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                <Loader2 size={48} className="spinning" style={{ color: 'var(--primary-color)' }} />
            </div>
        );
    }

    return (
        <div className="page">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <Shield size={28} />
                        Roles y Permisos
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                        Configura los niveles de acceso del sistema
                    </p>
                </div>
                <button 
                    onClick={() => { resetForm(); setModalOpen(true); }}
                    className="btn-primary"
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                    <Plus size={18} /> Nuevo Rol
                </button>
            </div>

            {/* Roles Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1rem' }}>
                {roles.map(role => (
                    <div key={role.id} className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
                        {/* Role Header */}
                        <div style={{ 
                            padding: '1.25rem',
                            borderBottom: expandedRole === role.id ? '1px solid var(--border-color)' : 'none'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <h3 style={{ 
                                        margin: '0 0 0.25rem 0', 
                                        fontSize: '1.125rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <Shield size={18} style={{ color: 'var(--primary-color)' }} />
                                        {role.nombre}
                                    </h3>
                                    <p style={{ 
                                        margin: 0, 
                                        fontSize: '0.875rem', 
                                        color: 'var(--text-secondary)' 
                                    }}>
                                        {role.descripcion || 'Sin descripción'}
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button 
                                        onClick={() => openEdit(role)}
                                        style={{
                                            background: 'rgba(79, 70, 229, 0.1)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.5rem',
                                            cursor: 'pointer',
                                            color: 'var(--primary-color)'
                                        }}
                                    >
                                        <Edit size={16} />
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(role.id)}
                                        style={{
                                            background: 'rgba(239, 68, 68, 0.1)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.5rem',
                                            cursor: 'pointer',
                                            color: 'var(--danger-color)'
                                        }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Stats */}
                            <div style={{ 
                                display: 'flex', 
                                gap: '1.5rem', 
                                marginTop: '1rem',
                                paddingTop: '1rem',
                                borderTop: '1px solid var(--border-color)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Users size={16} style={{ color: 'var(--text-secondary)' }} />
                                    <span style={{ fontSize: '0.875rem' }}>
                                        <strong>{role.usuarios_count || 0}</strong> usuarios
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Lock size={16} style={{ color: 'var(--text-secondary)' }} />
                                    <span style={{ fontSize: '0.875rem' }}>
                                        <strong>{role.permisos_count || 0}</strong> permisos
                                    </span>
                                </div>
                            </div>

                            {/* Expand Button */}
                            <button
                                onClick={() => fetchRolePermisos(role.id)}
                                style={{
                                    width: '100%',
                                    marginTop: '1rem',
                                    padding: '0.5rem',
                                    background: 'var(--bg-input)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    color: 'var(--text-secondary)',
                                    fontSize: '0.875rem'
                                }}
                            >
                                {expandedRole === role.id ? (
                                    <>Ocultar permisos <ChevronUp size={16} /></>
                                ) : (
                                    <>Ver permisos <ChevronDown size={16} /></>
                                )}
                            </button>
                        </div>

                        {/* Expanded Permissions */}
                        {expandedRole === role.id && rolePermisos[role.id] && (
                            <div style={{ padding: '1rem', background: 'var(--bg-input)' }}>
                                {rolePermisos[role.id].length === 0 ? (
                                    <p style={{ 
                                        textAlign: 'center', 
                                        color: 'var(--text-secondary)',
                                        margin: 0,
                                        padding: '1rem'
                                    }}>
                                        Sin permisos asignados
                                    </p>
                                ) : (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        {rolePermisos[role.id].map(perm => (
                                            <span 
                                                key={perm.id}
                                                style={{
                                                    background: 'white',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '9999px',
                                                    fontSize: '0.75rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                    border: '1px solid var(--border-color)'
                                                }}
                                            >
                                                <Check size={12} style={{ color: 'var(--secondary-color)' }} />
                                                {perm.nombre}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Modal */}
            {modalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
                        <button 
                            className="modal-close" 
                            onClick={() => { setModalOpen(false); resetForm(); }}
                        >
                            <X size={20} />
                        </button>
                        
                        <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Shield size={24} />
                            {editingId ? 'Editar Rol' : 'Nuevo Rol'}
                        </h2>

                        <form onSubmit={handleSubmit}>
                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                                <label>Nombre del Rol *</label>
                                <input
                                    required
                                    value={formData.nombre}
                                    onChange={e => setFormData({...formData, nombre: e.target.value})}
                                    placeholder="Ej: Supervisor"
                                />
                            </div>

                            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                                <label>Descripción</label>
                                <input
                                    value={formData.descripcion}
                                    onChange={e => setFormData({...formData, descripcion: e.target.value})}
                                    placeholder="Descripción del rol y sus responsabilidades"
                                />
                            </div>

                            {/* Permissions by Module - Table format */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ 
                                    fontWeight: '600', 
                                    marginBottom: '1rem', 
                                    display: 'block' 
                                }}>
                                    Permisos por Módulo
                                </label>

                                <div style={{ 
                                    border: '1px solid var(--border-color)', 
                                    borderRadius: 'var(--radius-md)',
                                    overflow: 'hidden'
                                }}>
                                    {/* Header */}
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: '1fr 80px 80px 80px',
                                        background: 'var(--bg-input)',
                                        padding: '0.75rem 1rem',
                                        fontWeight: '600',
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        color: 'var(--text-secondary)',
                                        borderBottom: '1px solid var(--border-color)'
                                    }}>
                                        <div>Módulo</div>
                                        <div style={{ textAlign: 'center' }}>Ver</div>
                                        <div style={{ textAlign: 'center' }}>Editar</div>
                                        <div style={{ textAlign: 'center' }}>Eliminar</div>
                                    </div>

                                    {/* Rows */}
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {permisosGrouped.map((group, idx) => {
                                            const perms = getPermisosForModulo(group.modulo, group.permisos);
                                            return (
                                                <div 
                                                    key={group.modulo}
                                                    style={{ 
                                                        display: 'grid', 
                                                        gridTemplateColumns: '1fr 80px 80px 80px',
                                                        padding: '0.75rem 1rem',
                                                        alignItems: 'center',
                                                        borderBottom: idx < permisosGrouped.length - 1 ? '1px solid var(--border-color)' : 'none',
                                                        background: 'white'
                                                    }}
                                                >
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        gap: '0.5rem',
                                                        fontSize: '0.875rem'
                                                    }}>
                                                        <span>{getModuloIcon(group.modulo)}</span>
                                                        <span style={{ fontWeight: '500' }}>{getModuloNombre(group.modulo)}</span>
                                                    </div>
                                                    
                                                    {/* Ver */}
                                                    <div style={{ textAlign: 'center' }}>
                                                        {perms.ver && (
                                                            <label style={{ cursor: 'pointer' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.permisos.includes(perms.ver.id)}
                                                                    onChange={() => togglePermiso(perms.ver.id)}
                                                                    style={{ display: 'none' }}
                                                                />
                                                                <div style={{
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    borderRadius: '6px',
                                                                    border: `2px solid ${formData.permisos.includes(perms.ver.id) ? 'var(--secondary-color)' : 'var(--border-color)'}`,
                                                                    background: formData.permisos.includes(perms.ver.id) ? 'var(--secondary-color)' : 'white',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    margin: '0 auto',
                                                                    transition: 'all 0.2s'
                                                                }}>
                                                                    {formData.permisos.includes(perms.ver.id) && (
                                                                        <Check size={14} color="white" />
                                                                    )}
                                                                </div>
                                                            </label>
                                                        )}
                                                    </div>

                                                    {/* Editar */}
                                                    <div style={{ textAlign: 'center' }}>
                                                        {perms.editar && (
                                                            <label style={{ cursor: 'pointer' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.permisos.includes(perms.editar.id)}
                                                                    onChange={() => togglePermiso(perms.editar.id)}
                                                                    style={{ display: 'none' }}
                                                                />
                                                                <div style={{
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    borderRadius: '6px',
                                                                    border: `2px solid ${formData.permisos.includes(perms.editar.id) ? 'var(--primary-color)' : 'var(--border-color)'}`,
                                                                    background: formData.permisos.includes(perms.editar.id) ? 'var(--primary-color)' : 'white',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    margin: '0 auto',
                                                                    transition: 'all 0.2s'
                                                                }}>
                                                                    {formData.permisos.includes(perms.editar.id) && (
                                                                        <Check size={14} color="white" />
                                                                    )}
                                                                </div>
                                                            </label>
                                                        )}
                                                    </div>

                                                    {/* Eliminar */}
                                                    <div style={{ textAlign: 'center' }}>
                                                        {perms.eliminar && (
                                                            <label style={{ cursor: 'pointer' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.permisos.includes(perms.eliminar.id)}
                                                                    onChange={() => togglePermiso(perms.eliminar.id)}
                                                                    style={{ display: 'none' }}
                                                                />
                                                                <div style={{
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    borderRadius: '6px',
                                                                    border: `2px solid ${formData.permisos.includes(perms.eliminar.id) ? 'var(--danger-color)' : 'var(--border-color)'}`,
                                                                    background: formData.permisos.includes(perms.eliminar.id) ? 'var(--danger-color)' : 'white',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    margin: '0 auto',
                                                                    transition: 'all 0.2s'
                                                                }}>
                                                                    {formData.permisos.includes(perms.eliminar.id) && (
                                                                        <Check size={14} color="white" />
                                                                    )}
                                                                </div>
                                                            </label>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Quick actions */}
                                <div style={{ 
                                    display: 'flex', 
                                    gap: '0.5rem', 
                                    marginTop: '0.75rem',
                                    justifyContent: 'flex-end'
                                }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const allIds = permisosGrouped.flatMap(g => g.permisos.map(p => p.id));
                                            setFormData(prev => ({ ...prev, permisos: allIds }));
                                        }}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            fontSize: '0.75rem',
                                            background: 'var(--bg-input)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: 'var(--radius-md)',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Seleccionar Todo
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, permisos: [] }))}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            fontSize: '0.75rem',
                                            background: 'var(--bg-input)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: 'var(--radius-md)',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Limpiar
                                    </button>
                                </div>
                            </div>

                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    onClick={() => { setModalOpen(false); resetForm(); }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--border-color)',
                                        background: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingId ? 'Actualizar' : 'Crear Rol'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
